import type { FieldPermissionsRule } from '../../../form-hooks/fields/FieldPermissions/FieldPermisssions';
import type {
  ContentTypeDefinition,
  UserRole,
  UserRolesPermission,
  WithOptionalInternalProps,
} from '../../../lib/flotiq-client/types/api-response';

type UserRolePermission = Omit<
  UserRolesPermission,
  'id' | 'contentTypeDefinition'
> & {
  id?: string;
  contentTypeDefinition?: {
    id: string | null;
    metaDefinition?: {
      order: string[];
      propertiesConfig: Record<string, unknown>;
    };
    required?: string[];
  } | null;
};

export type RolePermissionFormValues = Pick<
  UserRole,
  'name' | 'description'
> & {
  ctd_permissions: UserRolePermission[];
  co_permissions: UserRolePermission[];
};

export const getInitialValues = (
  role: WithOptionalInternalProps<UserRole> | undefined | null,
  ruleColumn: FieldPermissionsRule[],
  contentTypes: ContentTypeDefinition[] = [],
  duplicate = false,
): RolePermissionFormValues => {
  const allPermissions = (role?.permissions || [])?.map(
    ({ id, ...permission }) => {
      const ctdId = permission.contentTypeDefinition?.id;
      const contentType = contentTypes.find(({ id }) => ctdId === id);
      const order = contentType?.metaDefinition?.order;
      return {
        ...permission,
        id: duplicate ? undefined : id,
        contentTypeDefinition: ctdId
          ? {
              id: ctdId,
              metaDefinition: contentType?.metaDefinition,
              required: contentType?.schemaDefinition?.required,
            }
          : { id: null },
        attributesPermission: getInitialAttributes(
          permission,
          ruleColumn,
          order,
        ),
      };
    },
  );

  return {
    name: role?.name || '',
    description: role?.description || '',
    ctd_permissions: allPermissions.filter(({ type }) => type === 'CTD'),
    co_permissions: allPermissions.filter(({ type }) => type === 'CO'),
  };
};

const getInitialAttributes = (
  permission: Omit<UserRolesPermission, 'id'>,
  ruleColumn: FieldPermissionsRule[],
  order?: string[],
) => {
  if (permission.type === 'CTD' || !permission.attributesPermission) return;

  const newAttributes = ruleColumn
    .filter(({ isFieldPermission }) => isFieldPermission)
    .reduce(
      (acc, { name }) => {
        const ruleName = name as keyof typeof permission.attributesPermission;
        if (permission.attributesPermission?.[ruleName]?.length) {
          acc[name] = permission.attributesPermission[ruleName];
        } else if (
          permission[ruleName] &&
          !permission.attributesPermission?.[ruleName] &&
          order
        ) {
          acc[name] = order;
        }
        return acc;
      },
      {} as Record<string, string[]>,
    );
  return Object.keys(newAttributes).length > 0 ? newAttributes : undefined;
};
